import { Button, Card } from "@ths/ui";
import type { FC } from "react";

import type { SelfCheckinLookupReservation } from "../generated/graphql";

import {
  formatShortDisplayDate,
  formatStayRange,
  getStayDisplayDates,
} from "./dateFormatting";

interface BasecampSelectStepProps {
  reservations: SelfCheckinLookupReservation[];
  onSelect: (reservation: SelfCheckinLookupReservation) => void;
  onCancel?: () => void;
  flow?: "checkin" | "checkout" | "request" | "session" | "menu";
  upcomingOnly?: boolean;
}

export const BasecampSelectStep: FC<BasecampSelectStepProps> = ({
  reservations,
  onSelect,
  onCancel,
  flow = "checkin",
  upcomingOnly = false,
}) => {
  const firstName = reservations[0]?.guestFirstName ?? "";
  const isKnownGuestFlow = flow === "session" || flow === "menu";
  const orderedReservations = isKnownGuestFlow
    ? [...reservations].sort(
        (a, b) =>
          Number(Boolean(b.checkedInAt)) - Number(Boolean(a.checkedInAt)),
      )
    : reservations;

  return (
    <div className="bc-fade-up w-full max-w-sm space-y-4 text-on-navy sm:space-y-6">
      <div className="text-center">
        <h1 className="font-oswald text-2xl font-medium uppercase tracking-wide sm:text-3xl">
          {upcomingOnly
            ? "Choose an upcoming stay"
            : flow === "request"
              ? `Hi ${firstName}, which stay is this issue about?`
              : flow === "checkout"
                ? `Hi ${firstName}, which stay are you checking out of?`
                : isKnownGuestFlow
                  ? `Hi ${firstName}, choose a stay`
                  : `Hi ${firstName}, which stay are you checking in to?`}
        </h1>
        {flow === "menu" && (
          <p className="mt-2 text-body text-on-navy/75">
            Choose a stay for guest services.
          </p>
        )}
      </div>

      <ul className="space-y-3">
        {orderedReservations.map((r) => {
          const dates = getStayDisplayDates(r);
          return (
            <li key={r.assignmentId}>
              <Card
                as="button"
                interactive
                surface="bordered"
                onClick={() => onSelect(r)}
                className="w-full break-words border-on-navy/20 bg-navy/60 p-5 text-left text-on-navy hover:bg-navy hover:shadow-none hover:ring-on-navy/30 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong active:shadow-none"
              >
                <p className="text-heading text-on-navy">{r.basecampName}</p>
                {isKnownGuestFlow && (
                  <p className="mt-2 text-label text-on-navy/80">
                    {r.checkedInAt ? "Current stay" : "Upcoming reservation"}
                  </p>
                )}
                <p className="mt-1 text-body text-on-navy/75">
                  {isKnownGuestFlow && !dates.checkOut
                    ? `${r.checkedInAt ? "Since" : "Starts"} ${formatShortDisplayDate(
                        dates.checkIn,
                      )}`
                    : formatStayRange(dates.checkIn, dates.checkOut)}
                </p>
                {isKnownGuestFlow && (
                  <p className="mt-3 text-label text-on-navy">
                    {flow === "menu"
                      ? "Continue"
                      : r.checkedInAt
                        ? "View stay"
                        : "Continue"}{" "}
                    →
                  </p>
                )}
              </Card>
            </li>
          );
        })}
      </ul>

      {onCancel && (
        <Button
          variant="ghost"
          type="button"
          onClick={onCancel}
          className="w-full text-on-navy/90 hover:bg-on-navy/10 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
        >
          {isKnownGuestFlow ? `Not ${firstName}? Start over` : "Go Back"}
        </Button>
      )}
    </div>
  );
};

export default BasecampSelectStep;
